/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useRef } from 'preact/hooks';
import { BaseLegend } from './BaseLegend';
import { useEvents } from './useEvents';
import { LegendSection, SectionalLegendProps } from './Legend.type';
import { getSectionalNavUtils } from './SectionalLegendNavUtils';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { getDatatipText, getAriaProps, getRandomId, getGridNums } from './LegendUtils';
import { useLegendTooltip } from './useLegendTooltip';
import { LegendText } from './LegendText';
import { useItemFocus } from './useItemFocus';
import { useLegendLayout } from './useLegendLayout';
import { Flex } from '../UNSAFE_Flex';
import { styles } from './themes/LegendStyles.css';

/**
 * A Sectional Legend allows grouping of legend items in sections with a specific title.
 */
export function SectionalLegend<K extends string | number>({
  orientation = 'horizontal',
  sectionTitleHAlign = 'start',
  sections,
  isReadOnly = true,
  onItemAction,
  onInput,
  ...props
}: SectionalLegendProps<K>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const rootDimsRef = useRef<DOMRect>();

  const { getItem, getDetailFromInfo, getPrevItemInfo, getNextItemInfo } =
    getSectionalNavUtils(sections);

  const { layoutInfo, isColumnWidthValidRef } = useLegendLayout({
    rootRef,
    rootDimsRef,
    items: sections,
    isSectional: true,
    orientation
  });
  const { focusedItemInfo, hoveredItemInfo, ...eventsProps } = useEvents(
    !isReadOnly,
    props.hiddenIds != null,
    getDetailFromInfo,
    getPrevItemInfo,
    getNextItemInfo,
    onItemAction,
    onInput
  );

  const { focusedItemRef } = useItemFocus(focusedItemInfo);
  const text = getDatatipText(sections, getItem, focusedItemInfo, hoveredItemInfo);
  const { tooltipContent, tooltipProps } = useLegendTooltip({
    text,
    rootDimsRef,
    focusedItemInfo,
    focusedItemRef
  });
  const ariaProps = getAriaProps(isReadOnly, props['aria-label'], props.hiddenIds);
  const mergedProps = mergeProps(eventsProps, tooltipProps);
  const titleStyle = {
    fontFamily: props.sectionTitleFontFamily,
    fontSize: props.sectionTitleFontSize,
    color: props.sectionTitleColor,
    fontStyle: props.sectionTitleFontStyle,
    fontWeight: props.sectionTitleFontWeight,
    textDecoration: props.sectionTitleTextDecoration
  };
  const { sectionalLegendBaseStyles } = styles;
  const isHoriz = orientation === 'horizontal';

  return (
    <div
      ref={rootRef}
      tabIndex={isReadOnly ? undefined : 0}
      class={sectionalLegendBaseStyles}
      {...ariaProps}
      {...mergedProps}>
      <Flex direction={isHoriz ? 'row' : 'column'} wrap={'wrap'} gap={'4x'}>
        {sections.map((section: LegendSection<K>, index: number) => {
          const labelledById = !isReadOnly ? getRandomId() : '';
          const isWrappedSection = layoutInfo[index]?.isWrapped;
          const flexDirection = !isHoriz || isWrappedSection ? 'column' : 'row';
          const alignItems = !isHoriz || isWrappedSection ? 'start' : 'center';
          const { itemsPerCol, itemsPerRow } = getGridNums(section.items.length, layoutInfo[index]);
          return (
            <Flex
              align={alignItems}
              direction={flexDirection}
              maxWidth={'100%'}
              justify={'start'}
              wrap="wrap">
              <LegendText
                text={section.title}
                {...titleStyle}
                type="title"
                id={labelledById}
                align={sectionTitleHAlign}
              />
              <BaseLegend
                {...props}
                isColumnWidthValidRef={isColumnWidthValidRef}
                focusedItemRef={focusedItemRef}
                labelledBy={labelledById}
                sectionIdx={index}
                items={section.items}
                orientation={orientation}
                itemsPerRow={itemsPerRow}
                itemsPerCol={itemsPerCol}
                itemWidth={
                  layoutInfo[index]?.isWrapped ? layoutInfo[index]?.maxItemWidth : undefined
                }
                isReadOnly={isReadOnly}
                focusedItemInfo={focusedItemInfo}
                hoveredItemInfo={hoveredItemInfo}
                activeId={eventsProps['aria-activedescendant']}
              />
            </Flex>
          );
        })}
      </Flex>
      {tooltipContent}
    </div>
  );
}
